<template>
    <message-standard-details :messageStandardId="props.messageStandardId">
        <form-wrapper
            :sections="[
                { id: 'name', label: 'Name' },
                { id: 'description', label: 'Description' },
                { id: 'marketing-rules', label: 'Marketing' },
                { id: 'transactional-rules', label: 'Transactional' },
            ]"
            :hideNav="Boolean(props.messageStandardId)"
        >
            <vx-expandable-card id="name">
                <template v-slot:header> Name </template>
                <template v-slot:content>
                    <vx-input
                        data-test="name"
                        autofocus
                        v-model="messageStandard.name"
                        size="lg"
                        :error="displayFirstError(validation.name.$errors)"
                    ></vx-input>
                </template>
            </vx-expandable-card>

            <vx-expandable-card id="description">
                <template v-slot:header> Description </template>
                <template v-slot:content>
                    <vx-input
                        data-test="description"
                        v-model="messageStandard.description"
                        size="lg"
                        :rows="4"
                        :error="displayFirstError(validation.description.$errors)"
                    ></vx-input>
                </template>
            </vx-expandable-card>

            <message-standard-rules-form
                ref="rulesForm"
                v-model="messageStandard"
                :showRulesInitially="!props.messageStandardId"
                v-model:marketingEnabled="marketingRulesEnabled"
                v-model:transactionalEnabled="transactionalRulesEnabled"
            />

            <vx-sticky-footer>
                <vx-button :href="`/settings/teams/${teamId}#/message-standards`" class="w-20" size="lg" color="muted">
                    Cancel
                </vx-button>
                <vx-button
                    @click="submit()"
                    data-test="submit-button"
                    class="grow"
                    size="lg"
                    color="primary"
                    :loading="processing"
                >
                    {{ props.messageStandardId ? 'Update' : 'Create' }} Standard
                </vx-button>
            </vx-sticky-footer>
        </form-wrapper>
    </message-standard-details>
</template>

<script setup lang="ts">
import MessageStandardDetails from './MessageStandardDetails.vue';
import { VxButton, VxStickyFooter, VxExpandableCard, VxInput } from '@voxie/frontend-components';
import { faSquareArrowUpLeft } from '@fortawesome/pro-solid-svg-icons';
import { nextTick, ref } from 'vue';
import { required, maxLength } from '@vuelidate/validators';
import { alphanumericDashUnderscoreSpace, displayFirstError } from '~/utils/validation';
import { messageStandardsClient } from '~/services';
import FormWrapper from '~/components/general/FormWrapper.vue';
import { useFormSubmission } from '~/composables/useFormSubmission.js';
import type {} from '~/services/messageStandards/messageStandardsClient.types';
import MessageStandardRulesForm from './MessageStandardRulesForm.vue';
import { readMessageStandard, useMessageStandardDetails } from './useMessageStandardDetails';

const teamId = Spark.state.currentTeam.id;

const props = defineProps<{
    messageStandardId?: string | number;
}>();

const { messageStandard, onMountedMessageStandard } = useMessageStandardDetails();

const rulesForm = ref<InstanceType<typeof MessageStandardRulesForm>>();
const marketingRulesEnabled = ref(true);
const transactionalRulesEnabled = ref(true);

onMountedMessageStandard(async () => {
    await nextTick();

    if (props.messageStandardId && rulesForm.value) {
        marketingRulesEnabled.value = Boolean(rulesForm.value.marketingCount);
        transactionalRulesEnabled.value = Boolean(rulesForm.value.transactionalCount);
    }
});

const recentlyCreatedId = ref<number>();

const save = async () => {
    const data = { ...messageStandard.value };

    if (!marketingRulesEnabled.value) {
        data.marketing_rules = undefined;
    }

    if (messageStandard.value.transactional_config !== null) {
        data.transactional_rules = undefined;
    }

    if (!transactionalRulesEnabled.value) {
        data.transactional_config = undefined;
        data.transactional_rules = undefined;
    }

    if (!props.messageStandardId) {
        const response = await messageStandardsClient.createMessageStandard({ teamId }, data);
        recentlyCreatedId.value = response.data.id;
    } else {
        const response = await messageStandardsClient.updateMessageStandard(
            { teamId },
            Number(props.messageStandardId),
            data
        );
        messageStandard.value = readMessageStandard(response.data);
    }
};

const { submit, processing, validation } = useFormSubmission({
    data: messageStandard,
    validation: {
        name: {
            required,
            maxLength: maxLength(80),
            format: alphanumericDashUnderscoreSpace,
        },
        description: {
            maxLength: maxLength(2000),
        },
    },
    preserveScroll: () => Boolean(props.messageStandardId),
    submit: save,
    notification: () => ({
        message: props.messageStandardId
            ? `Brand Standard updated`
            : '<strong>New Brand Standard</strong> created successfully. Add targets below.',
        redirect: recentlyCreatedId.value
            ? `/settings/teams/${teamId}/message-standards/${recentlyCreatedId.value}/targets`
            : undefined,
        action: {
            icon: faSquareArrowUpLeft,
            label: 'Back to Standards',
            href: `/settings/teams/${teamId}#/message-standards`,
        },
    }),
});
</script>
